export class ServiceContainer {
  private static services: Map<Symbol, any> = new Map<Symbol, any>();
  private static multicastServices: Map<Symbol, any[]> = new Map<Symbol, any[]>();

  public static register<TService>(
    service: RegisterServiceType<TService>
  ): RegisterBuilder<TService> {
    return new RegisterBuilder({ registerInstance: ServiceContainer.registerInstance }, service);
  }

  public static addInstance<TInstance>(instance: TInstance) {
    return new AddInstanceBuilder<TInstance>(
      { registerInstance: ServiceContainer.registerInstance },
      instance
    );
  }

  static getRequired<TService>(service: ServiceTypeInfo<TService>): TService {
    const a = this.get<TService>(service);
    if (a === null) {
      throw new Error(`No service registered for requested type`);
    }

    return a;
  }

  private static registerInstance<TService>(
    service: RegisterServiceType<TService>,
    instance: TService
  ) {
    if (typeof service === 'function') {
      if (!('serviceType' in service)) {
        throw new Error(
          `Service ${
            (service as Function).name
          } does not have a serviceType. If a class add the @Service decorator.`
        );
      }
      const aService = instance as any as ServiceTypeInfo<TService>;
      // Don't register the same instance twice
      if (ServiceContainer.services.get(aService.serviceType)?.includes(instance)) {
        // biome-ignore lint/suspicious/noConsole: Intentional console usage
        console.warn(`Service ${service.toString()} already registered, ignoring`);
        return;
      }

      ServiceContainer.services.set(aService.serviceType, instance);
    } else if ('serviceType' in service) {
      // Don't register the same instance twice
      if (ServiceContainer.services.get(service.serviceType)?.includes(instance)) {
        // biome-ignore lint/suspicious/noConsole: Intentional console usage
        console.warn(`Service ${service.toString()} already registered, ignoring`);
        return;
      }

      ServiceContainer.services.set(service.serviceType, instance);
    } else if ('multicastServiceType' in service) {
      const services = ServiceContainer.multicastServices.get(service.multicastServiceType) ?? [];
      services.push(instance);
      ServiceContainer.multicastServices.set(service.multicastServiceType, services);
    }
  }

  /**
   * Get a service from the container
   * @param serviceType The service type to get
   */
  static get<TService>(serviceType: ServiceTypeInfo<TService>): TService | null;

  /**
   * Gets a multicast service from the container
   * @param serviceType The service type to get
   */
  static get<TService>(serviceType: MulticastServiceTypeInfo<TService>): TService[];

  /**
   * Get a service from the container
   * @param serviceType The service type to get
   */
  static get<TService>(
    serviceType: ServiceTypeInfo<TService> | MulticastServiceTypeInfo<TService>
  ): TService | TService[] | null {
    if ('multicastServiceType' in serviceType) {
      return this.multicastServices.get((serviceType as any).multicastServiceType) ?? [];
    }

    const service = this.services.get((serviceType as any).serviceType);
    return service ?? null;
  }

  static reset() {
    this.services.clear();
    this.multicastServices.clear();
    this.config.clear();
  }

  static get config() {
    return ConfigContainer;
  }
}

class ConfigContainer {
  private static configs: Map<string, any> = new Map<string, any>();

  static get<T extends ValueType>(type: ConfigTypeInfo<T>): T['defaultValue'] {
    const config = this.configs.get(type.name);
    return config ?? type.defaultValue;
  }

  static set<T extends ValueType>(type: ConfigTypeInfo<T>, value: T['defaultValue']) {
    this.configs.set(type.name, value);
  }

  static setFromObject(playerConfigurations: Record<string, any>) {
    Object.keys(playerConfigurations).forEach(key => {
      this.configs.set(key, playerConfigurations[key]);
    });
  }

  static getAll() {
    const result: Record<string, any> = {};
    this.configs.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }

  static clear() {
    this.configs.clear();
  }
}

/**
 * Creates a service type where the service container will only allow one instance of the service
 */
export function createServiceType<T>() {
  return {
    serviceType: Symbol('createServiceType'),
  } as ServiceTypeInfo<T>;
}

/**
 * Creates a service type where the service container will allow multiple instances of the service
 */
export function createMulticastServiceType<T>() {
  return {
    multicastServiceType: Symbol('createMulticastServiceType'),
  } as MulticastServiceTypeInfo<T>;
}

export type ValueType<TOptionType = string> =
  | {
      defaultValue: boolean;
      valueType: 'boolean';
    }
  | {
      defaultValue: TOptionType;
      valueType: 'enum';
      options: TOptionType[];
    };

/**
 * Creates a service type where the service container will only allow one instance of the service
 */
export function createConfigType<T extends ValueType<TOptions>, TOptions extends string>(
  name: string,
  valueTypeDefault: T
) {
  return {
    name,
    ...valueTypeDefault,
  } as ConfigTypeInfo<T>;
}

export type ServiceTypeInfo<T> = { serviceType: symbol };
export type MulticastServiceTypeInfo<T> = { multicastServiceType: symbol };

export type ConfigTypeInfo<T extends ValueType> = {
  name: string;
} & T;

/**
 * A register builder that first takes a service type and then allows you to register multiple instances of that service
 */
class RegisterBuilder<TService> {
  #serviceType: RegisterServiceType<TService>;
  #serviceContainer: ServiceContainerBuilderType;

  constructor(
    serviceContainer: ServiceContainerBuilderType,
    serviceType: RegisterServiceType<TService>
  ) {
    this.#serviceType = serviceType;
    this.#serviceContainer = serviceContainer;
  }

  instance = (instance: TService) => {
    this.#serviceContainer.registerInstance(this.#serviceType, instance);
    return this;
  };
}

/**
 * A register builder that allows you to register an instance of a service to multiple service types
 */
class AddInstanceBuilder<TInstance> {
  #instance: TInstance;
  #serviceContainer: ServiceContainerBuilderType;

  constructor(serviceContainer: ServiceContainerBuilderType, instance: TInstance) {
    this.#instance = instance;
    this.#serviceContainer = serviceContainer;
  }

  as = (serviceType: RegisterServiceType<TInstance>) => {
    this.#serviceContainer.registerInstance(serviceType, this.#instance);
    return this;
  };
}

/**
 * A helper type that contains all the types of services that can be registered
 */
type RegisterServiceType<TService> =
  | ServiceTypeInfo<TService>
  | MulticastServiceTypeInfo<TService>
  | Function;

type ServiceContainerBuilderType = {
  registerInstance: <TService>(service: RegisterServiceType<TService>, instance: TService) => void;
};
